// src/components/Review/ReviewHelpMenu.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ReviewHelpMenu.css";

const FAQ_ITEMS = [
  {
    key: "review-button",
    title: "How does the Review button work?",
    body: (
      <>
        <p>
          The 👁 Review button shows <b>who still needs to review relevant notes/replies</b> for this
          VOD (grouped by team role).
        </p>

        <ul>
          <li>
            A user name is <b>green</b> when they are fully caught up (no unreviewed relevant items).
          </li>
          <li>
            If a user has unreviewed items:
            <ul>
              <li>
                <b>Players</b> show in the “missing-player” color (more urgent).
              </li>
              <li>
                <b>Non-players</b> (subs/analysts/coaches/managers/spectators) show in the
                “missing-nonplayer” color.
              </li>
            </ul>
          </li>
          <li>
            The small fraction under the eye shows <b>caught up / total</b> for that VOD.
          </li>
          <li>
            The eye icon itself turns “all reviewed” (green) only when <b>all players</b> are caught up.
          </li>
        </ul>

        <p style={{ marginTop: "0.6rem" }}>
          <b>What counts as “relevant”?</b>
        </p>
        <ul>
          <li>
            <b>@username</b>: always relevant to that specific user.
          </li>
          <li>
            <b>@Team</b>: relevant only for users in roles that are expected to act on team-wide review
            (currently: <b>player</b>, <b>analyst</b>, <b>coach</b>).
          </li>
          <li>
            <b>Managers / spectators / subs</b>: will <b>not</b> be required to review @Team items unless
            directly @mentioned.
          </li>
        </ul>

        <p style={{ marginTop: "0.6rem" }}>
          <b>Replies:</b> if someone replies to your note, that reply can still require your review until you view it.
        </p>
      </>
    ),
  },
  {
    key: "vod-ready",
    title: "How does the “Video ready for review” button work?",
    body: (
      <>
        <p>
          The 📣 button is a quick way to alert teammates that a primary POV VOD is ready to be reviewed.
        </p>
        <ul>
          <li>It is meant to notify the team and drive people into the VOD review flow.</li>
          <li>Use it once the VOD has the important POV uploaded and is ready for feedback/notes.</li>
        </ul>
      </>
    ),
  },
  {
    key: "video-tags",
    title: "How does the Video tags function work?",
    body: (
      <>
        <p>
          The 🏷 tags icon shows the tags applied to the match/POV. Tags help your team filter and find VODs faster.
        </p>
        <ul>
          <li>Hovering the 🏷 icon shows the tag list.</li>
          <li>Use the Filter panel to include/exclude tags and narrow down the VOD list.</li>
          <li>
            “Not Reviewed by Me” is a special filter to surface VODs where you still have relevant items left.
          </li>
        </ul>
      </>
    ),
  },
];

export default function ReviewHelpMenu() {
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(FAQ_ITEMS[0].key);

  const rootRef = useRef(null);
  const closeTimerRef = useRef(null);

  const activeItem = useMemo(
    () => FAQ_ITEMS.find((x) => x.key === activeKey) || FAQ_ITEMS[0],
    [activeKey]
  );

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openNow = () => {
    cancelClose();
    setOpen(true);
  };

  // Delay close so moving cursor from ? -> panel doesn't instantly close it
  const closeSoon = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 160);
  };

  // Close on click-outside
  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => cancelClose();
  }, []);

  return (
    <div className="RevHelp-root" ref={rootRef}>
      <button
        type="button"
        className="RevHelp-btn"
        aria-label="Help / FAQ"
        title="Help / FAQ"
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        onFocus={openNow}
        onBlur={closeSoon}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v); // click toggle (mobile-friendly)
        }}
      >
        ?
      </button>

      {open && (
        <div
          className="RevHelp-panel"
          role="dialog"
          aria-label="Review FAQ"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
        >
          <div className="RevHelp-panel-inner">
            <div className="RevHelp-left">
              <div className="RevHelp-title">FAQ</div>
              {FAQ_ITEMS.map((item) => {
                const isActive = item.key === activeKey;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`RevHelp-q ${isActive ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveKey(item.key);
                    }}
                  >
                    {item.title}
                  </button>
                );
              })}
            </div>

            <div className="RevHelp-right">
              <div className="RevHelp-ans-title">{activeItem.title}</div>
              <div className="RevHelp-ans-body">{activeItem.body}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
